import { isValidEmail } from '@Utils/helpers/stringHelpers';
import { ITableEditOption } from '@Utils/helpers/tableHelpers';
import { Switch } from '@mui/material';
import { CircularProgress } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '@Components/Button/Button';
import CircularLoading from '@Components/CircularLoading/CircularLoading';
import SwitchIIPStyledComponent from '@Components/DomElements/Switch';
import SettingsTable, {
    SettingsTableViewState,
} from '@Components/Settings/components/SettingsTable/SettingsTable';
import Styles from '@Components/Settings/views/SettingsViews.scss';
import OrganizationsApi from '@Data/organizations/Api';
import {
    IOrganization,
    IUpdateOrganization,
} from '@Data/organizations/Interfaces';
import { organizationsState } from '@Data/organizations/Reducer';
import SettingsTableButton from '../../../components/SettingsTable/SettingsTableButton';
import { ISettingsBaseComponentProps } from '../../Interfaces';

const emptyUpdateOrganization: IUpdateOrganization = {
    owner_email: '',
    organisation_name: '',
    is_active: true,
};

const OrganizationSettingsTable = (props: ISettingsBaseComponentProps) => {
    const { title, isSubmitting, setIsSubmitting, id } = props;
    const organizations = useSelector(organizationsState);
    const [selectedOrganization, setSelectedOrganization] =
        useState<IOrganization>(null);
    const [tableState, setTableState] = useState<SettingsTableViewState>(
        SettingsTableViewState.VIEW
    );
    const [updateOrganizationEntity, setUpdateOrganizationEntity] =
        useState<IUpdateOrganization>(emptyUpdateOrganization);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const includeEmail = true;

    useEffect(() => {
        handleGetOrgs();
    }, []);

    const handleGetOrgs = async () => {
        setIsLoading(true);
        await OrganizationsApi.getOrganizations(includeEmail);
        setIsLoading(false);
    };

    const columnHelper = createColumnHelper<IOrganization>();
    const columns = [
        columnHelper.accessor('owner_email', {
            cell: (info) => info.getValue(),
            header: 'Org Admin IKE ID',
        }),
        columnHelper.accessor('organisation_name', {
            cell: (info) => info.getValue(),
            header: 'Organization Name',
        }),
        columnHelper.accessor('is_active', {
            cell: (info) => {
                const isActive = info.getValue();
                return SwitchIIPStyledComponent(isActive);
            },
            header: 'Active',
        }),
    ];

    const submitCleanup = async () => {
        setSelectedOrganization(null);
        handleCancelUpdateOrganization();
        setIsSubmitting(false);
        await handleGetOrgs();
    };

    const handleUpdateOrganization = async () => {
        setIsSubmitting(true);

        if (tableState === SettingsTableViewState.CREATE) {
            await OrganizationsApi.createOrganization(
                updateOrganizationEntity.organisation_name,
                updateOrganizationEntity.owner_email,
                updateOrganizationEntity.is_active
            );
        } else if (tableState === SettingsTableViewState.EDIT) {
            await OrganizationsApi.updateOrganization(
                selectedOrganization.organisation_id,
                updateOrganizationEntity.organisation_name,
                updateOrganizationEntity.owner_email,
                updateOrganizationEntity.is_active
            );
        }

        submitCleanup();
    };

    const handleSetEditState = () => {
        setUpdateOrganizationEntity({
            owner_email: selectedOrganization.owner_email,
            organisation_name: selectedOrganization.organisation_name,
            is_active: selectedOrganization.is_active,
        });
        setTableState(SettingsTableViewState.EDIT);
    };

    const handleCancelUpdateOrganization = () => {
        setTableState(SettingsTableViewState.VIEW);
        setUpdateOrganizationEntity(emptyUpdateOrganization);
    };

    const canCreateNewEntity =
        tableState === SettingsTableViewState.CREATE &&
        updateOrganizationEntity.organisation_name !== '' &&
        isValidEmail(updateOrganizationEntity.owner_email);

    const canEditEntity =
        tableState === SettingsTableViewState.EDIT &&
        updateOrganizationEntity.organisation_name !== '' &&
        isValidEmail(updateOrganizationEntity.owner_email);

    const editCells: Array<ITableEditOption> = [
        {
            attribute: 'owner_email',
            component: <input id="owner-email-input" type="text" />,
        },
        {
            attribute: 'organisation_name',
            component: <input id="organization-name-input" type="text" />,
        },
        { attribute: 'is_active', component: <Switch /> },
    ];

    const createCells: Array<ITableEditOption> = [
        {
            attribute: 'owner_email',
            component: <input id="owner-email-input" type="text" />,
        },
        {
            attribute: 'organisation_name',
            component: <input id="organization-name-input" type="text" />,
        },
        { attribute: 'is_active', component: <Switch /> },
    ];

    return (
        <div className={Styles.settingsViewWrapper}>
            <div className={Styles.settingsViewHeader}>
                <span id={`${id}-table-title`}>{title}</span>

                <div>
                    {tableState !== SettingsTableViewState.VIEW && (
                        <Button
                            id="settings-cancel-edit-org-btn"
                            color={'black'}
                            onClick={handleCancelUpdateOrganization}
                        >
                            Cancel
                        </Button>
                    )}

                    <SettingsTableButton
                        id={`edit-${id}-btn`}
                        tableState={tableState}
                        canEdit={!_isNil(selectedOrganization)}
                        createText={'Create Organization'}
                        editText={'Edit Organization'}
                        setCreateState={() =>
                            setTableState(SettingsTableViewState.CREATE)
                        }
                        setEditState={handleSetEditState}
                        onCreateNewEntity={handleUpdateOrganization}
                        onEditEntity={handleUpdateOrganization}
                        canCreateNewEntity={canCreateNewEntity}
                        canEditEntity={canEditEntity}
                        isSubmitting={isSubmitting}
                    />
                </div>
            </div>
            <div className={Styles.tableContainer}>
                {isLoading ? (
                    <CircularLoading />
                ) : (
                    <SettingsTable
                        id={`${id}-table`}
                        columns={columns}
                        data={organizations}
                        onRowClick={(org) => setSelectedOrganization(org)}
                        selectedRow={selectedOrganization}
                        state={tableState}
                        updateEntity={updateOrganizationEntity}
                        onChangeUpdateEntity={setUpdateOrganizationEntity}
                        editCells={editCells}
                        createCells={createCells}
                        isSubmitting={isSubmitting}
                    />
                )}
            </div>
        </div>
    );
};

export default OrganizationSettingsTable;
